import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Skeleton } from "@nexus/ui";
import {
  adminPendingHandoffsQueryOptions,
  useRetryAdminOrderHandoff,
} from "@/lib/queries";
import { handoffBadgeVariant } from "@/features/marketplace/lib/fulfillment-badge-variant";

interface AdminPendingHandoffsListProps {
  canList: boolean;
  canRetry: boolean;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function AdminPendingHandoffsList({ canList, canRetry }: AdminPendingHandoffsListProps) {
  const { t } = useTranslation();
  const { data: rows = [], isLoading, isError } = useQuery({
    ...adminPendingHandoffsQueryOptions(),
    enabled: canList,
  });
  const retryMutation = useRetryAdminOrderHandoff();

  if (!canList) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("common.noPermission", "You don't have permission to view this content.")}
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title={t("marketplace.admin.handoffs.errorTitle", "Could not load pending handoffs")}
        description={t("errors.tryAgain", "Please try again.")}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title={t("marketplace.admin.handoffs.empty", "No pending partner emails")}
        description={t(
          "marketplace.admin.handoffs.emptyDescription",
          "Orders waiting to email partners, or where delivery failed, appear here.",
        )}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-3">{t("marketplace.admin.handoffs.table.tenant", "Client")}</th>
            <th className="px-4 py-3">{t("marketplace.admin.handoffs.table.vendor", "Partner")}</th>
            <th className="px-4 py-3">{t("marketplace.admin.handoffs.table.recipient", "To")}</th>
            <th className="px-4 py-3">{t("marketplace.admin.handoffs.table.status", "Status")}</th>
            <th className="px-4 py-3">{t("marketplace.admin.handoffs.table.queued", "Queued")}</th>
            {canRetry ? (
              <th className="px-4 py-3 text-right">
                {t("marketplace.admin.handoffs.table.actions", "Actions")}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-3 font-medium">{row.tenant.name}</td>
              <td className="px-4 py-3">{row.vendor.name}</td>
              <td className="px-4 py-3 text-muted-foreground">{row.recipientEmail}</td>
              <td className="px-4 py-3">
                <Badge variant={handoffBadgeVariant(row.status)} dot>
                  {t(`marketplace.orders.status.${row.status}`)}
                </Badge>
              </td>
              <td className="px-4 py-3 tabular-nums text-muted-foreground">
                {formatDateTime(row.createdAt)}
              </td>
              {canRetry ? (
                <td className="px-4 py-3 text-right">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={retryMutation.isPending && retryMutation.variables === row.id}
                    onClick={() => retryMutation.mutate(row.id)}
                  >
                    {retryMutation.isPending && retryMutation.variables === row.id
                      ? t("marketplace.orders.handoff.retrying")
                      : t("marketplace.orders.handoff.retry")}
                  </Button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
